import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { MdAssignment } from "react-icons/md";
import { RiCalendarTodoFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "../../../components/student/Layout/layout";
import SolveHomework from "../../../containers/student/models/solve-homework";
import { openSolveHomeworkModel } from "../../../modules/admin-modules/models/Actions";
import { getHomeworksByChapterIdRequest } from "../../../modules/students-modules/homework/Actions";

const C = {
  primary: "#E85D04",
  secondary: "#9A3412",
  border: "#E8E0D5",
  cream: "#FFF7ED",
  paper: "#FFFFFF",
  mutedBg: "#F5F0E8",
  muted: "#78716C",
};

const FILE_PREVIEW_URL =
  "https://docs.google.com/gview?url=https://api.asquera.com/public/homework/";

function statusMeta(studentHomework) {
  if (!studentHomework) {
    return { label: "Not submitted", bg: C.muted };
  }
  switch (Number(studentHomework.status)) {
    case 0:
      return { label: "Pending", bg: C.primary };
    case 1:
      return { label: "Accepted", bg: "#15803D" };
    case 2:
      return { label: "Refused", bg: "#B91C1C" };
    default:
      return { label: "Unknown", bg: C.muted };
  }
}

function actionLabel(studentHomework) {
  const hasFile = studentHomework?.file;
  const status = studentHomework?.status;
  if ((status === 0 || status === 1) && hasFile) return "Preview";
  if (status === 2) return "Resubmit";
  return "Solve";
}

const StudentHomeWork = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedData, setSelectedData] = useState();
  const chapterHomework = useSelector(
    (state) => state.studentHomework.chapterHomeworks
  );
  const isLoading = useSelector((state) => state.studentHomework.isLoading);
  const dispatch = useDispatch();
  const chapterName = searchParams.get("name") || "Homeworks";
  const homeworks = Array.isArray(chapterHomework) ? chapterHomework : [];

  useEffect(() => {
    if (id) dispatch(getHomeworksByChapterIdRequest(id));
  }, [id, dispatch]);

  const handleAction = (data) => {
    const studentHomework = data?.student_homework;
    const hasFile = studentHomework?.file;
    const status = studentHomework?.status;

    if ((status === 0 || status === 1) && hasFile) {
      window.open(`${FILE_PREVIEW_URL}${hasFile}`, "_blank");
      return;
    }

    if (status === 2 || !studentHomework) {
      setSelectedData(data);
      dispatch(openSolveHomeworkModel());
    }
  };

  return (
    <Layout color="main_1" bg="white" isLoading={isLoading}>
      <Container maxW="container.2xl" h="100%" my="100px" mb="165px" w="100%">
        <VStack spacing={8} h="100%" w="100%" alignItems="stretch">
          <Box w="100%" maxW="4xl" mx="auto">
            <Flex mb={4}>
              <Text
                as={Link}
                to="/homeworks/sections"
                fontSize="sm"
                fontWeight="700"
                color={C.muted}
                _hover={{ color: C.primary }}
              >
                ← Back to sections
              </Text>
            </Flex>

            <Flex
              align="center"
              justify="space-between"
              borderBottomWidth="2px"
              borderColor={C.primary}
              pb={4}
              mb={8}
              flexWrap="wrap"
              gap={4}
            >
              <Heading
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="800"
                color={C.primary}
                textTransform="uppercase"
                letterSpacing="tight"
              >
                {chapterName}
              </Heading>
            </Flex>

            {isLoading && homeworks.length === 0 ? (
              <VStack spacing={4} align="stretch">
                {[1, 2, 3].map((k) => (
                  <Skeleton key={k} h="180px" borderRadius="xl" />
                ))}
              </VStack>
            ) : homeworks.length === 0 ? (
              <Text color={C.muted} fontWeight="600">
                No homeworks available for this chapter yet.
              </Text>
            ) : (
              <VStack spacing={4} align="stretch">
                {homeworks.map((data) => {
                  const studentHomework = data?.student_homework;
                  const status = statusMeta(studentHomework);
                  const label = actionLabel(studentHomework);
                  const isSelected = selectedData?.id === data?.id;
                  const isOutline = label !== "Preview";

                  return (
                    <Box
                      key={data?.id}
                      bg={isSelected ? C.mutedBg : C.paper}
                      borderWidth="1px"
                      borderColor={isSelected ? C.primary : C.border}
                      borderRadius="xl"
                      overflow="hidden"
                      transition="border-color 0.2s, background 0.2s, box-shadow 0.2s"
                      _hover={{
                        borderColor: C.primary,
                        boxShadow:
                          "0 18px 46px rgba(232, 93, 4, 0.12), 0 0 0 1px rgba(232, 224, 213, 0.35)",
                      }}
                    >
                      <Box
                        px={5}
                        py={3}
                        bgGradient="linear(to-r, rgba(232, 93, 4, 0.12), rgba(154, 52, 18, 0.04))"
                        borderBottomWidth="1px"
                        borderColor={C.border}
                      >
                        <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                          <HStack spacing={3} minW={0}>
                            <Box
                              bg={C.cream}
                              color={C.secondary}
                              borderRadius="lg"
                              p={2}
                              display="flex"
                              flexShrink={0}
                            >
                              <Icon as={MdAssignment} boxSize="22px" />
                            </Box>
                            <VStack align="flex-start" spacing={0} minW={0}>
                              <Text
                                fontSize="md"
                                fontWeight="800"
                                color={C.primary}
                                noOfLines={2}
                              >
                                {data?.name}
                              </Text>
                              {data?.created_at && (
                                <HStack spacing={1.5} color={C.muted}>
                                  <Icon as={RiCalendarTodoFill} fontSize="12px" />
                                  <Text
                                    fontSize="11px"
                                    fontWeight="700"
                                    textTransform="uppercase"
                                    letterSpacing="0.04em"
                                  >
                                    {dayjs(data.created_at).format("D MMM YYYY")}
                                  </Text>
                                </HStack>
                              )}
                            </VStack>
                          </HStack>
                          <Badge
                            bg={status.bg}
                            color="white"
                            fontSize="9px"
                            fontWeight="800"
                            textTransform="uppercase"
                            letterSpacing="0.06em"
                            px={2.5}
                            py={1}
                            borderRadius="full"
                          >
                            {status.label}
                          </Badge>
                        </Flex>
                      </Box>

                      <VStack align="stretch" spacing={4} p={5}>
                        {data?.homework ? (
                          <Box
                            bg={C.mutedBg}
                            borderRadius="lg"
                            px={4}
                            py={3}
                            borderWidth="1px"
                            borderColor={C.border}
                          >
                            <Text
                              fontSize="10px"
                              fontWeight="800"
                              textTransform="uppercase"
                              letterSpacing="0.06em"
                              color={C.muted}
                              mb={2}
                            >
                              Homework
                            </Text>
                            <Text
                              fontSize="sm"
                              color={C.secondary}
                              fontWeight="600"
                              whiteSpace="pre-wrap"
                              lineHeight="1.7"
                            >
                              {data.homework}
                            </Text>
                          </Box>
                        ) : null}

                        {studentHomework?.evaluation && (
                          <Box>
                            <Text
                              fontSize="10px"
                              fontWeight="800"
                              textTransform="uppercase"
                              letterSpacing="0.06em"
                              color={C.muted}
                              mb={1}
                            >
                              Evaluation
                            </Text>
                            <Text
                              fontSize="sm"
                              color={C.secondary}
                              whiteSpace="pre-wrap"
                              lineHeight="1.6"
                            >
                              {studentHomework.evaluation}
                            </Text>
                          </Box>
                        )}

                        {studentHomework?.comment && (
                          <Box>
                            <Text
                              fontSize="10px"
                              fontWeight="800"
                              textTransform="uppercase"
                              letterSpacing="0.06em"
                              color={C.muted}
                              mb={1}
                            >
                              Comment
                            </Text>
                            <Text
                              fontSize="sm"
                              color={C.muted}
                              whiteSpace="pre-wrap"
                              lineHeight="1.6"
                            >
                              {studentHomework.comment}
                            </Text>
                          </Box>
                        )}

                        <Button
                          alignSelf={{ base: "stretch", md: "flex-end" }}
                          w={{ base: "100%", md: "auto" }}
                          minW="140px"
                          size="sm"
                          variant={isOutline ? "outline" : "solid"}
                          bg={isOutline ? "transparent" : C.primary}
                          borderWidth="2px"
                          borderColor={C.primary}
                          color={isOutline ? C.primary : "white"}
                          fontWeight="800"
                          textTransform="uppercase"
                          fontSize="10px"
                          borderRadius="lg"
                          isDisabled={data?.active === 0}
                          onClick={() => handleAction(data)}
                          _hover={
                            isOutline
                              ? { bg: `${C.primary}12` }
                              : { bg: C.secondary }
                          }
                          _disabled={{
                            opacity: 0.55,
                            cursor: "not-allowed",
                            bg: isOutline ? "transparent" : C.primary,
                            color: isOutline ? C.primary : "white",
                            borderColor: C.primary,
                          }}
                        >
                          {label}
                        </Button>
                      </VStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </VStack>
        <SolveHomework data={selectedData} setSelectedData={setSelectedData} />
      </Container>
    </Layout>
  );
};

export default StudentHomeWork;
